import { useThumbnailLabels } from "hooks/useThumbnailLabels";
import type { Feature, Project, Resource } from "models";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import ImageGallery from "react-image-gallery";
import ImageUploading, {
  type ErrorsType,
  type ImageListType,
  type ImageType,
  type ImageUploadingPropsType,
} from "react-images-uploading";
import { ProjectService, ResourceService } from "sections/projects";
import { Message } from "semantic-ui-react";
import { useUI } from "services";
import { USER_MSG, getTranslatedUserMsg } from "strings";
import { getImageIdFromUrl, logger } from "../utils";
import { useT } from "i18n";
import { ProjectImageList } from "./project/ProjectImageList";
import { UploadImageButton } from "./project/UploadImageButton";
import { uploadProjectImage } from "./project/UploadProjectImage";
import { NEW_PROJECT_ID_STRING } from "../sections/projects/ProjectDetail";

const DATA_KEY = "url";
const MAX_QTY = 1;

const THUMBNAIL_WIDTH = 120;

async function getFeatureImageOrder(feature: Feature): Promise<string[]> {
  try {
    const rawPointCloudResource = feature.resources?.find(
      (res) => res.type === "raw point cloud",
    );
    const pointCloudId = rawPointCloudResource?.id;
    const pointCloudRevision = rawPointCloudResource?.revision;
    const pointsImageMapId = feature.pointImageMap;

    if (!pointsImageMapId || !pointCloudId || pointCloudRevision == null) {
      return [];
    }

    const pointCloudData = await ResourceService.getRawData(
      pointCloudId,
      pointCloudRevision,
    );
    const uuidRegex =
      /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;
    const pointIDs = pointCloudData.match(uuidRegex) || [];

    if (pointIDs.length === 0) {
      return [];
    }

    const pointToImageMap = await ResourceService.getRawDataAsJson<
      Record<string, string>
    >(pointsImageMapId, 0);

    const imageIDs = pointIDs
      .map((pointId: string) => pointToImageMap[pointId])
      .filter(Boolean);

    return imageIDs;
  } catch (error) {
    console.error("Error fetching feature image order:", error);
    return [];
  }
}

async function getFeaturesImageOrderMap(
  features: Feature[],
): Promise<Map<string, string[]>> {
  if (!features || features.length === 0) {
    return new Map();
  }

  const orderMap = new Map<string, string[]>();

  // Fetch ordering for all features in parallel and continue on individual failures
  const results = await Promise.allSettled(
    features.map((feature) => {
      return getFeatureImageOrder(feature);
    }),
  );

  results.forEach((result, index) => {
    if (result.status === "fulfilled" && result.value.length > 0) {
      orderMap.set(features[index].id, result.value);
    }
    // Intentionally ignore rejections to keep building the map for other features
  });

  return orderMap;
}

function getImageToFeatureMap(
  featureOrderMap: Map<string, string[]>,
): Map<string, string[]> {
  const imageToFeatureMap = new Map<string, string[]>();
  const imageToFeatureSet = new Map<string, Set<string>>();

  featureOrderMap.forEach((imageIds, featureId) => {
    imageIds.forEach((imageId) => {
      let featureSet = imageToFeatureSet.get(imageId);
      if (!featureSet) {
        featureSet = new Set<string>();
        imageToFeatureSet.set(imageId, featureSet);
      }

      if (!featureSet.has(featureId)) {
        featureSet.add(featureId);
        imageToFeatureMap.set(imageId, Array.from(featureSet));
      }
    });
  });

  return imageToFeatureMap;
}

export interface ImageUploadProps {
  project: Project;
  projId: string;
  setProjId: React.Dispatch<React.SetStateAction<string>>;
  className: string;
  inEditMode?: boolean;
  onError: () => void;
  actualProjectId: string;
  features?: Feature[]; // Features for image ordering
}

interface ThumnailImage extends ImageType {
  id: string;
  url: string;
  thumbnailUrl: string;
  source?: string;
  pending?: boolean;
  deleteable?: boolean;
}

export default function ImageUpload({
  project,
  className,
  projId,
  setProjId,
  inEditMode = false,
  onError,
  actualProjectId,
  features = [],
}: ImageUploadProps): React.ReactElement {
  const t = useT();
  const getErrors = () => ({
    QTY: t("component.imageUpload.maxFiles", "You can upload at most {{count}} files", {
      count: MAX_QTY,
    }),
    TYPE: t("component.imageUpload.unsupportedFileType", "Unsupported file type"),
    SIZE: t(
      "component.imageUpload.imageSizeMax",
      "Image size must be 2 MB max, video size 20 MB max",
    ),
    RES: t(
      "component.imageUpload.imageResolutionWrong",
      "Image resolution is wrong (?)",
    ),
  });
  const ERRORS = getErrors();
  const IPAD_IMAGE_LABEL = t("component.imageUpload.ipadImage", "iPad Image");
  const gallery = useRef<ImageGallery | null>(null);
  const [images, setImages] = useState<ThumnailImage[]>([]);
  const [fullSizeImages, setFullSizeImages] = useState<ImageListType>([]);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [orderedImageIds, setOrderedImageIds] = useState<string[]>([]);
  const [featureOrderMap, setFeatureOrderMap] = useState<Map<string, string[]>>(
    new Map(),
  );
  const [featureLabels, setFeatureLabels] = useState<string[]>([]);

  const [galleryVisible, setIsGalleryVisible] = useState(false);
  const { showError, showSuccess } = useUI();

  const uploadImage = uploadProjectImage(project, projId, showSuccess, showError, t);

  const removeImage = (
    image: ImageType,
    index: number,
    onImageRemove: { (index: number): void; (arg0: any): any },
  ) => {
    const newImages = images.filter((img, galleryIndex) => galleryIndex !== index);

    if (image.file) {
      setImages(newImages);
      onImageRemove(index);
    } else {
      const imageID = getImageIdFromUrl(image.url);
      logger.trace("Image ID to delete %s", imageID);

      ResourceService.del(imageID)
        .then(
          () => {
            onImageRemove(index);
            setImages(newImages);
          },
          () => {
            showError();
          },
        )
        .catch((err) => {
          logger.error(err);
          throw err;
        });
    }
  };

  // Upload images if editing existing project, otherwise flag with pending to upload on create
  const onChange: ImageUploadingPropsType["onChange"] = useCallback(
    (newImages, indexes) => {
      if (indexes?.length) {
        const image = newImages[indexes[0]];
        if (project.id || projId) {
          setUploading(true);
          uploadImage(image)
            .then(() => {
              setUploading(false);
              setImages([image as ThumnailImage, ...images]);
            })
            .catch((error) => {
              throw error;
            });
        } else {
          image.pending = true;
          setImages([image as ThumnailImage, ...images]);
        }
      }
    },
    [project.id, uploadImage, images],
  );

  const onImageClick = (index: number) => {
    if (inEditMode) {
      return;
    } else {
      gallery.current?.slideToIndex(index);
      gallery.current?.fullScreen();
    }
  };

  const getErrorMsgs = (errors: ErrorsType) => {
    if (!errors) return;
    const msg = [];
    if (errors.maxNumber) msg.push(ERRORS.QTY);
    if (errors.acceptType) msg.push(ERRORS.TYPE);
    if (errors.maxFileSize) msg.push(ERRORS.SIZE);
    if (errors.resolution) msg.push(ERRORS.RES);
    return msg;
  };

  // Helper function to order any array of objects with an id property
  const orderByIds = useCallback(
    <T extends { id: string }>(items: T[], orderedIds: string[]): T[] => {
      if (!orderedIds.length) return items;

      const itemMap = new Map(items.map((item) => [item.id, item]));
      const ordered: T[] = [];
      const usedIds = new Set<string>();

      // Add items in the order specified by orderedIds
      orderedIds.forEach((id) => {
        const item = itemMap.get(id);
        if (item) {
          ordered.push(item);
          usedIds.add(id);
        }
      });

      // Append remaining items that weren't in the ordered list
      items.forEach((item) => {
        if (!usedIds.has(item.id)) {
          ordered.push(item);
        }
      });

      return ordered;
    },
    [],
  );

  // Fetch ordered image IDs from features
  useEffect(() => {
    // Early return if no features to process - prevents unnecessary state updates and async calls
    if (!features || features.length === 0) {
      setFeatureOrderMap(new Map());
      setOrderedImageIds([]);
      return;
    }

    // Fetch ordering for all features
    async function fetchOrderedImageIds() {
      try {
        const orderMap = await getFeaturesImageOrderMap(features);
        const allOrderedIds: string[] = [];

        // Combine all ordered IDs from all features
        orderMap.forEach((ids) => {
          allOrderedIds.push(...ids);
        });

        setOrderedImageIds(allOrderedIds);
        setFeatureOrderMap(orderMap);
      } catch (error) {
        logger.error("Failed to fetch ordered image IDs");
        logger.error(error);
      }
    }

    fetchOrderedImageIds();
  }, [features]);

  // Generate feature labels for watermarks
  useEffect(() => {
    if (!fullSizeImages.length || !features.length || !featureOrderMap.size) {
      setFeatureLabels([]);
      return;
    }

    // Create image-to-feature mapping
    const imageToFeatureMap = getImageToFeatureMap(featureOrderMap);

    // Create feature lookup by ID
    const featureById = new Map(features.map((f) => [f.id, f]));

    // Track counts per feature type
    const typeCounts = new Map<string, number>();

    // Generate labels for each image
    const labels = fullSizeImages.map((image) => {
      const imageId = (image as ImageType & { id: string }).id;
      if (!imageId) {
        return IPAD_IMAGE_LABEL;
      }

      const featureIds = imageToFeatureMap.get(imageId);
      if (!featureIds || featureIds.length === 0) {
        return IPAD_IMAGE_LABEL;
      }

      // Use the first feature found
      const featureId = featureIds[0];
      const feature = featureById.get(featureId);
      if (!feature || !feature.type) {
        return IPAD_IMAGE_LABEL;
      }

      // Get or increment count for this feature type
      const currentCount = typeCounts.get(feature.type) || 0;
      const newCount = currentCount + 1;
      typeCounts.set(feature.type, newCount);

      return `${feature.type} ${newCount}`;
    });

    setFeatureLabels(labels);
  }, [fullSizeImages, features, featureOrderMap]);

  // Upload images added during project creation
  useEffect(() => {
    if (!projId || uploading) return;
    const pending = images.filter((img) => img.pending).map(uploadImage);
    if (pending.length) {
      setUploading(true);
      Promise.all(pending)
        .then(() => {
          showSuccess(getTranslatedUserMsg(t, "SUCCESS_IMAGE"));
        }, onError)
        .finally(() => {
          setProjId("");
          setUploading(false);
        });
    }
  }, [projId, images, uploading, uploadImage, onError]);

  /*
    IMAGE FETCHING ON MOUNT
  */
  useEffect(() => {
    // If the user is creating a project or the project has no resources, we don't need to fetch images
    if (
      !actualProjectId ||
      actualProjectId === NEW_PROJECT_ID_STRING ||
      !project.resources ||
      !project.resources.length ||
      inEditMode
    ) {
      return;
    }
    try {
      // Fetch thumbnail images & display loading spinner until they are retrieved
      setLoading(true);
      ProjectService.getImages(actualProjectId, THUMBNAIL_WIDTH).then(
        (thumbnailUrls) => {
          const thumbnailImages: ThumnailImage[] = thumbnailUrls.map((url) => {
            const imageId = getImageIdFromUrl(url);
            // If we have the capture source in project.resources,
            // populate from there to save on network calls
            const source = project.resources?.find(
              (resource) => resource.id === imageId,
            )?.captureSource;
            const deleteable =
              (source === "mobile" || source === "portal") &&
              imageId !== project.primaryImageId;

            return {
              id: imageId,
              url,
              thumbnailUrl: url,
              source,
              deleteable,
            };
          });

          // Apply ordering to thumbnails
          const orderedThumbnailImages = orderByIds(thumbnailImages, orderedImageIds);

          // We have enough to display the thumbnail images,
          // so set those to state & hide the spinner.
          setImages(orderedThumbnailImages);
          setLoading(false);

          // Fetch full-size image urls
          ProjectService.getImages(actualProjectId).then((result) => {
            // Create thumbnail lookup map for matching
            const thumbnailMap = new Map(
              orderedThumbnailImages.map((thumb) => [thumb.id, thumb]),
            );

            // Create full-size images with IDs
            const fullSizeImagesWithIds = result.map((imageUrl) => {
              const imageId = getImageIdFromUrl(imageUrl);
              const thumbnail = thumbnailMap.get(imageId);
              return {
                id: imageId,
                url: imageUrl,
                thumbnailUrl: thumbnail?.thumbnailUrl ?? thumbnail?.url ?? imageUrl,
              } as ImageType & { id: string };
            });

            // Apply ordering to full-size images (same order as thumbnails)
            const orderedFullSizeImages = orderByIds(
              fullSizeImagesWithIds,
              orderedImageIds,
            );

            setFullSizeImages(orderedFullSizeImages);
          });
          // Sometimes the getImages() call returns images that aren't
          // in project.resources (not sure why). We still need their
          // captureSource to know if they can be deleted, so let's fetch them.
          Promise.all(
            orderedThumbnailImages
              .filter((image: ThumnailImage) => !image.source)
              .map((image: ThumnailImage) => {
                return ResourceService.get(image.id).then((result: Resource) => ({
                  id: result.id,
                  url: result.url!,
                  thumbnailUrl: result.url!,
                  source: result.captureSource,
                  deleteable:
                    (result.captureSource === "mobile" ||
                      result.captureSource === "portal") &&
                    result.id !== project.primaryImageId,
                }));
              }),
          ).then((thumbnailsWithCaptureSource: ThumnailImage[]) => {
            const finalImages = orderedThumbnailImages.map((image: ThumnailImage) => {
              if (image.source) {
                return image;
              }
              const match = thumbnailsWithCaptureSource.find(
                (thumbnail: ThumnailImage) => thumbnail.id === image.id,
              );
              if (match) {
                return match;
              }
              return image;
            });
            setImages(finalImages);
          });
        },
      );
    } catch (error) {
      // If any call fails, hide the spinner & show/log an error
      setLoading(false);
      showError({
        title: t(
          "component.imageUpload.errorFetchingProjectImages",
          "Error Fetching Project Images",
        ),
        msg: t("component.imageUpload.pleaseTryAgainLater", "Please try again later."),
      });
      logger.error("Failed getting project images");
      logger.error(error);
      throw error;
    }
  }, [inEditMode, orderedImageIds]);

  // Add thumbnail labels to gallery images
  useThumbnailLabels(galleryVisible, featureLabels);

  return (
    <ImageUploading
      value={inEditMode ? images : images.slice(0, 6)}
      onChange={inEditMode || !uploading ? onChange : () => false}
      dataURLKey={DATA_KEY}
    >
      {({
        imageList,
        onImageUpload,
        onImageRemove,
        //onImageUpdate,
        //onImageRemoveAll,
        isDragging,
        dragProps,
        errors,
      }) => {
        return (
          <div className={className}>
            {errors && <Message compact list={getErrorMsgs(errors)} />}

            <div className={"flex"}>
              {(inEditMode || loading || uploading) && (
                <UploadImageButton
                  dragging={isDragging}
                  onClick={onImageUpload}
                  loading={loading}
                  uploading={uploading}
                  disabled={!inEditMode || uploading}
                  dragProps={dragProps}
                />
              )}

              {!loading && (
                <div className={"carousel"} id={"image-gallery-carousel"}>
                  {imageList.map((image, index) => (
                    <ProjectImageList
                      key={index}
                      image={image}
                      index={index}
                      onOpen={() => {
                        onImageClick(index);
                      }}
                      canDelete={inEditMode && images[index].deleteable}
                      onClose={() => {
                        removeImage(image, index, onImageRemove);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            <>
              <ImageGallery
                lazyLoad={true}
                ref={gallery}
                items={fullSizeImages.map((image) => ({
                  original: image.url,
                  thumbnail: image.thumbnailUrl,
                }))}
                useBrowserFullscreen={false}
                onScreenChange={(isVisible) => {
                  setIsGalleryVisible(isVisible);
                }}
                thumbnailPosition={"top"}
                additionalClass={"abs hidden " + (galleryVisible ? "show" : "")}
                showPlayButton={false}
                showFullscreenButton={true}
              />
            </>
          </div>
        );
      }}
    </ImageUploading>
  );
}
